'use client';

import { useEffect, useCallback, useState, useMemo } from 'react';
import { Card, IconButton } from '@radix-ui/themes';
import { IconX } from '@tabler/icons-react';
import { trpcConsumerGetWebsiteCart } from '@trashlab/api/src/generated/hooks';
import { useShopSteps } from '@trashlab/sitesapp/src/components/shop/steps';

// Extend WindowEventMap for proper typing of custom events
declare global {
  interface WindowEventMap {
    'cart-modal-open': CustomEvent<{ cartId: string }>;
  }
}

/**
 * GlobalCartModal - Renders a cart/shop modal overlay when ?cart={id} is present in the URL.
 * 
 * This component should be placed in the root layout so it renders on TOP of any page.
 * The underlying page stays visible behind the semi-transparent overlay.
 * 
 * Benefits:
 * - URL is shareable/bookmarkable (e.g., /?cart=abc-123)
 * - Page content stays rendered behind the modal
 * - Works from any page in the app
 */
export const GlobalCartModal = () => {
  // Get cart ID from URL on mount and when URL changes
  const getCartIdFromUrl = useCallback(() => {
    if (typeof window === 'undefined') return null;
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('cart');
  }, []);

  const [cartId, setCartId] = useState<string | null>(getCartIdFromUrl);

  // Listen for cart-modal-open events and popstate (back/forward navigation)
  useEffect(() => {
    const handleCartOpen = (e: CustomEvent<{ cartId: string }>) => {
      setCartId(e.detail.cartId);
    };

    const handlePopState = () => {
      setCartId(getCartIdFromUrl());
    };

    // Also check URL on mount (for direct links with ?cart=...)
    setCartId(getCartIdFromUrl());

    window.addEventListener('cart-modal-open', handleCartOpen);
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('cart-modal-open', handleCartOpen);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [getCartIdFromUrl]);

  const { data: cartData, isLoading } = trpcConsumerGetWebsiteCart.useQuery(
    { id: cartId ?? '' },
    { enabled: !!cartId },
  );

  const shopSteps = useShopSteps({
    tenantId: cartData?.cart.organization.id ?? '',
  });

  const handleClose = useCallback(() => {
    // Remove cart param from URL while staying on current page
    const url = new URL(window.location.href);
    url.searchParams.delete('cart');
    window.history.pushState({}, '', url.toString());
    setCartId(null);
  }, []);

  // Handle escape key
  useEffect(() => {
    if (!cartId) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [cartId, handleClose]);

  // Prevent body scroll when modal is open and hide chat widget
  useEffect(() => {
    if (cartId) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      // Add class to hide chat widget while modal is open
      document.body.classList.add('shop-modal-open');
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.classList.remove('shop-modal-open');
      };
    }
  }, [cartId]);

  // useMemo must be called BEFORE any early returns to follow Rules of Hooks
  const currentStep = useMemo(
    () => shopSteps.find((step) => step.key === cartData?.cart.currentStepKey),
    [shopSteps, cartData?.cart.currentStepKey],
  );

  // Don't render anything if no cart ID
  if (!cartId) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Shopping cart"
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100000]"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
          handleClose();
        }
      }}
    >
      {/* Fullscreen on mobile, constrained on larger screens */}
      <Card className="w-full h-full sm:max-w-[min(1200px,calc(100vw-32px))] sm:h-[min(750px,calc(100vh-32px))] p-0 rounded-none sm:rounded-lg overflow-hidden flex flex-col relative bg-white">
        <IconButton
          className="absolute top-3 right-3 z-10 rounded-full cursor-pointer"
          color="gray"
          size="1"
          onClick={handleClose}
          aria-label="Close shopping cart"
        >
          <IconX />
        </IconButton>

        {isLoading || !cartData ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900" />
          </div>
        ) : (
          currentStep?.render({ cartData })
        )}
      </Card>
    </div>
  );
};

export default GlobalCartModal;
